/**
 * Runtime plugin registry.
 *
 * Reads userSettings.plugins[], fetches /manifest from each enabled plugin's
 * sidecar, and exposes a cache of loaded plugins keyed by id. The host UI
 * (worklist, viewer, settings) queries this cache to decide what to render
 * at each slot and which trigger moments to dispatch.
 *
 * Load pattern:
 *   - All manifests fetched in parallel (one fetch per enabled plugin)
 *   - Failures don't block the UI — the failed plugin stays in the list
 *     with loadError populated so the admin's Settings pane can show it
 *   - Reload on-demand (admin edits a plugin's baseUrl → refresh)
 *
 * Thread model: the registry's state is a single React-friendly object;
 * subscribers (hooks in the host) can either read .snapshot() for a
 * one-shot value or subscribe() for change notifications. No zustand
 * dependency — kept dependency-free so plugins remain host-agnostic.
 */

import type {
  LoadedRuntimePlugin,
  RuntimePluginConfig,
  RuntimePluginManifest,
  TriggerMoment,
  UiSlot,
} from './types';

const MANIFEST_FETCH_TIMEOUT_MS = 5000;

/** Internal mutable state. One per browser session. */
interface RegistryState {
  plugins: Map<string, LoadedRuntimePlugin>;
  listeners: Set<() => void>;
}

const state: RegistryState = {
  plugins: new Map(),
  listeners: new Set(),
};

/** Current registry snapshot — stable reference between loads. */
export function snapshot(): LoadedRuntimePlugin[] {
  return Array.from(state.plugins.values());
}

/** Get a specific plugin by id, or undefined. */
export function getPlugin(id: string): LoadedRuntimePlugin | undefined {
  return state.plugins.get(id);
}

/** Subscribe to registry changes. Returns unsubscribe. */
export function subscribe(fn: () => void): () => void {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}

function notify() {
  for (const fn of state.listeners) {
    try { fn(); } catch { /* swallow listener errors */ }
  }
}

/**
 * Load (or reload) all plugins. Call at app boot and whenever the Settings
 * pane saves changes to `userSettings.plugins`. Fetches run in parallel;
 * each plugin's loaded/loadError state reflects its own fetch outcome.
 *
 * NOTE: this does NOT short-circuit on cache — call reloadPlugin(id) for
 * a single-plugin refresh if you only need to re-check one.
 */
export async function loadRuntimePlugins(
  configs: RuntimePluginConfig[],
): Promise<LoadedRuntimePlugin[]> {
  const results = await Promise.all(
    configs
      .filter((c) => c.enabled)
      .map((c) => loadOne(c)),
  );

  // Replace state atomically — rebuild the map so stale entries from
  // deleted plugins fall out.
  state.plugins.clear();
  for (const r of results) {
    state.plugins.set(r.config.id, r);
  }
  notify();
  return results;
}

/** Reload a single plugin — used when the admin edits one entry in Settings. */
export async function reloadPlugin(
  config: RuntimePluginConfig,
): Promise<LoadedRuntimePlugin> {
  const r = await loadOne(config);
  state.plugins.set(r.config.id, r);
  notify();
  return r;
}

/** Remove a plugin from the registry — call after Settings deletes an entry. */
export function unloadPlugin(id: string): void {
  if (state.plugins.delete(id)) notify();
}

/** Core loader for one plugin. Never throws — all errors land in loadError. */
async function loadOne(config: RuntimePluginConfig): Promise<LoadedRuntimePlugin> {
  const baseUrl = config.baseUrl.replace(/\/+$/, '');

  try {
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), MANIFEST_FETCH_TIMEOUT_MS);
    let resp: Response;
    try {
      resp = await fetch(`${baseUrl}/manifest`, {
        signal: ac.signal,
        headers: { Accept: 'application/json' },
      });
    } finally {
      clearTimeout(t);
    }

    if (!resp.ok) {
      return {
        manifest: placeholderManifest(config, `HTTP ${resp.status}`),
        config,
        baseUrl,
        loaded: false,
        loadError: `GET /manifest returned HTTP ${resp.status}`,
      };
    }

    const manifest = (await resp.json()) as RuntimePluginManifest;

    // Sanity: declared manifest id must match the admin's config id.
    if (manifest.id !== config.id) {
      return {
        manifest,
        config,
        baseUrl,
        loaded: false,
        loadError:
          `Manifest id "${manifest.id}" does not match configured id "${config.id}". ` +
          `Check the plugin's baseUrl is pointing at the correct sidecar.`,
      };
    }

    return { manifest, config, baseUrl, loaded: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      manifest: placeholderManifest(config, msg),
      config,
      baseUrl,
      loaded: false,
      loadError: `Failed to fetch /manifest: ${msg}`,
    };
  }
}

/** Fallback manifest used when the fetch fails — gives the Settings UI
 *  something to render so the admin can see + edit the broken entry. */
function placeholderManifest(
  config: RuntimePluginConfig,
  errorHint: string,
): RuntimePluginManifest {
  return {
    id: config.id,
    name: config.displayName || config.id,
    version: '0.0.0-unreachable',
    category: 'utility',
    vendor: { name: 'Unknown (plugin unreachable)' },
    capabilities: {},
    description: `Plugin sidecar unreachable: ${errorHint}`,
  };
}

// ---------- Query helpers for the host UI ----------

/** All loaded plugins that fill a given UI slot. Stacking order follows the
 *  order plugins were loaded (userSettings.plugins[] order). */
export function pluginsForSlot(slot: UiSlot): LoadedRuntimePlugin[] {
  return snapshot().filter(
    (p) => p.loaded && (p.manifest.capabilities.uiSlots ?? []).includes(slot),
  );
}

/** All loaded plugins subscribed to a given trigger moment. The host
 *  iterates these and POSTs /trigger on each at the right time. */
export function pluginsForTrigger(moment: TriggerMoment): LoadedRuntimePlugin[] {
  return snapshot().filter((p) => {
    if (!p.loaded) return false;
    const triage = p.manifest.capabilities.triage;
    if (triage && triage.triggerMoments.includes(moment)) return true;
    // Future: other categories (reporting, routing, etc.) with their own
    // trigger-moment arrays will be or'd in here.
    return false;
  });
}
